// src/utils/helpers.js

/** ---------------------------
 *  SAFE STRING + TEXT HELPERS
 *  ---------------------------
 */

/** Normalize a value to a lowercase string safely (for search, compare). */
export const normalize = (str) => {
  if (str === null || str === undefined) return "";
  try {
    return str.toString().trim().toLowerCase();
  } catch (err) {
    // Defensive: never let a bad value crash search.
    console.warn("normalize() failed for:", str, err);
    return "";
  }
};

/** Slugify for ids/urls. Keeps your existing behavior. */
export const slugify = (s) =>
  (s || "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

/** Coerce any value to a safe string for UI (never "undefined" on screen). */
export const toText = (v, fallback = "") => {
  if (v === null || v === undefined) return fallback;
  try {
    const t = String(v).trim();
    return t.length ? t : fallback;
  } catch {
    return fallback;
  }
};

/** Get a number safely (for hymn numbers in inputs). */
export const toNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/** ---------------------------
 *  LYRICS UTILITIES
 *  ---------------------------
 */

/**
 * Return the "first line" preview for a hymn:
 * 1) Prefer first non-empty line from structured stanzas if available
 * 2) Fallback to first non-empty line from raw lyrics text
 */
export const getFirstLineFromLyrics = (hymn) => {
  try {
    // Prefer structured
    const stanzas = hymn?.lyrics_structured?.stanzas;
    if (Array.isArray(stanzas) && stanzas.length > 0) {
      // stanzas[i] should be an array of lines; be robust if not
      const firstBlock = Array.isArray(stanzas[0]) ? stanzas[0] : [stanzas[0]];
      const firstLine = (firstBlock || []).find(
        (ln) => normalize(ln).length > 0
      );
      if (firstLine) return toText(firstLine);
    }

    // Fallback to raw lyrics
    const raw = toText(hymn?.lyrics);
    if (raw) {
      const first = raw
        .replace(/\r\n/g, "\n")
        .split("\n")
        .map((l) => l.trim())
        .find((l) => l.length > 0);
      if (first) return first;
    }
  } catch (e) {
    console.warn("getFirstLineFromLyrics() error:", e);
  }
  return "";
};

/** ---------------------------
 *  DISPLAY HELPERS
 *  ---------------------------
 */

/** Build a compact "By; Author • Key: X" line (matches your Ghana style). */
export const buildMetaLine = (hymn) => {
  const author = toText(hymn?.author_name);
  const key = toText(hymn?.key_label);
  if (author && key) return `By; ${author}  •  Key: ${key}`;
  if (author) return `By; ${author}`;
  if (key) return `Key: ${key}`;
  return "";
};

/** Defensive sorter helpers */
export const byCreatedDesc = (a, b) => {
  const ta = a?.createdAt?._seconds ?? a?.createdAt?.seconds ?? 0;
  const tb = b?.createdAt?._seconds ?? b?.createdAt?.seconds ?? 0;
  return tb - ta;
};

export const byTitleAZ = (a, b) =>
  normalize(a?.title).localeCompare(normalize(b?.title));

export const byTitleZA = (a, b) =>
  normalize(b?.title).localeCompare(normalize(a?.title));

/** ---------------------------
 *  SEARCH / FILTER HELPERS
 *  ---------------------------
 */

/**
 * Return true if hymn matches query against title, author, key, first line, master number.
 * Uses normalize() so it won’t crash on null/undefined.
 */
export const hymnMatchesQuery = (hymn, qRaw) => {
  const q = normalize(qRaw);
  if (!q) return true;

  const title = normalize(hymn?.title);
  const author = normalize(hymn?.author_name);
  const key = normalize(hymn?.key_label);
  const number = normalize(hymn?.master_number);
  const firstLine = normalize(getFirstLineFromLyrics(hymn));

  return (
    title.includes(q) ||
    author.includes(q) ||
    key.includes(q) ||
    number.includes(q) ||
    firstLine.includes(q)
  );
};

/** Convenience: compactly render “#123 — Title” safely */
export const formatHymnHeading = (h) => {
  const num = toNumber(h?.master_number, null);
  const t = toText(h?.title, "Untitled Hymn");
  return num ? `#${num} — ${t}` : t;
};
